import React, { useEffect, useRef } from 'react';
import { createChart } from 'lightweight-charts';

const BaseChart = ({ data = [], chartType = 'candlestick', height = 500, timeframe = 5, onChartReady = null }) => {
  const chartContainerRef = useRef();
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartContainerRef.current || !data.length) {
      console.log('BaseChart: No container or data available');
      return;
    }

    // Clear any existing chart
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    console.log('BaseChart: Creating chart with', data.length, 'data points');

    // Create chart with minimal, clean configuration
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: height,
      layout: {
        background: { type: 'solid', color: '#ffffff' },
        textColor: '#333333',
      },
      grid: {
        vertLines: { color: '#e0e0e0' },
        horzLines: { color: '#e0e0e0' },
      },
      timeScale: {
        // CRITICAL: These are the key settings for time axis
        visible: true,
        timeVisible: true,
        secondsVisible: false,
        borderVisible: true,
        borderColor: '#cccccc',
        // Force specific time formatting
        tickMarkFormatter: (time) => {
          // Handle different time formats that lightweight-charts might pass
          let timestamp;
          if (typeof time === 'number') {
            timestamp = time;
          } else if (time && typeof time === 'object') {
            if (time.year && time.month && time.day) {
              // BusinessDay format
              const date = new Date(Date.UTC(time.year, time.month - 1, time.day));
              timestamp = Math.floor(date.getTime() / 1000);
            } else if (time.seconds) {
              timestamp = time.seconds;
            }
          }

          if (!timestamp) {
            return '';
          }

          // Format as readable time
          const date = new Date(timestamp * 1000);
          const day = date.getDate().toString().padStart(2, '0');
          const month = date.toLocaleDateString('en-IN', { month: 'short' });
          const hour = date.getHours().toString().padStart(2, '0');
          const minute = date.getMinutes().toString().padStart(2, '0');
          
          return `${day} ${month} ${hour}:${minute}`;
        },
      },
      rightPriceScale: {
        visible: true,
        borderVisible: true,
        borderColor: '#cccccc',
      },
      crosshair: {
        mode: 0, // Normal crosshair mode
        vertLine: {
          width: 1,
          color: '#999999',
          style: 0,
          labelVisible: false, // Hide default time label
        },
        horzLine: {
          width: 1,
          color: '#999999',
          style: 0,
          labelVisible: false, // Hide default price label
        },
      },
    });

    chartRef.current = chart;

    // Create the appropriate series
    let series;
    if (chartType === 'line') {
      series = chart.addLineSeries({
        color: '#2962FF',
        lineWidth: 2,
      });
    } else {
      // Default to candlestick
      series = chart.addCandlestickSeries({
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: false,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350',
      });
    }

    // Format data (keep original parsing, just add market hours filter later if needed)
    const formattedData = data.map(item => {
      // Convert timestamp to Unix timestamp (seconds) - original simple parsing
      let time;
      if (item.timestamp) {
        // Handle ISO string timestamps from backend
        const date = new Date(item.timestamp);
        time = Math.floor(date.getTime() / 1000);
      } else if (item.time) {
        time = typeof item.time === 'number' ? item.time : Math.floor(new Date(item.time).getTime() / 1000);
      } else {
        return null;
      }

      if (chartType === 'line') {
        return {
          time: time,
          value: parseFloat(item.close || item.value || 0)
        };
      } else {
        return {
          time: time,
          open: parseFloat(item.open || 0),
          high: parseFloat(item.high || 0),
          low: parseFloat(item.low || 0),
          close: parseFloat(item.close || 0),
        };
      }
    }).filter(item => item !== null);

    // Calculate last candle time based on timeframe
    // Market closes at 15:30, so last candle depends on timeframe
    let lastCandleMinute;
    if (timeframe === 1) {
      lastCandleMinute = 29; // 1-min: last candle at 15:29
    } else if (timeframe === 5) {
      lastCandleMinute = 25; // 5-min: last candle at 15:25
    } else if (timeframe === 15) {
      lastCandleMinute = 15; // 15-min: last candle at 15:15
    } else {
      lastCandleMinute = 25; // Default to 5-min behavior
    }

    // Filter out pre-open and post-market candles based on timeframe
    const marketHoursData = formattedData.filter(item => {
      if (!item.time) return false;
      
      // Convert Unix timestamp back to date for hour/minute check
      const date = new Date(item.time * 1000);
      const hour = date.getHours();
      const minute = date.getMinutes();
      
      // Market hours: 9:15 AM to calculated end time
      const isMarketHours = 
        (hour === 9 && minute >= 15) ||  // 9:15 AM onwards
        (hour >= 10 && hour <= 14) ||    // 10 AM to 2:59 PM
        (hour === 15 && minute <= lastCandleMinute); // Up to last candle time
      
      return isMarketHours;
    });

    console.log(`BaseChart: Timeframe ${timeframe}min - Last candle at 15:${lastCandleMinute.toString().padStart(2, '0')}. Filtered ${formattedData.length - marketHoursData.length} pre/post market candles. Using ${marketHoursData.length} market hours candles.`);

    // Set data and fit content
    series.setData(marketHoursData);
    
    // Add OHLC display on crosshair - create it in chart container
    let ohlcDiv = null;
    
    const createOHLCDiv = () => {
      if (ohlcDiv) return ohlcDiv;
      
      ohlcDiv = document.createElement('div');
      ohlcDiv.id = 'ohlc-display';
      ohlcDiv.style.position = 'absolute';
      ohlcDiv.style.top = '15px';
      ohlcDiv.style.left = '15px';
      ohlcDiv.style.background = 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.95) 50%, rgba(30, 41, 59, 0.95) 100%)';
      ohlcDiv.style.backdropFilter = 'blur(10px)';
      ohlcDiv.style.webkitBackdropFilter = 'blur(10px)';
      ohlcDiv.style.color = '#f8fafc';
      ohlcDiv.style.border = '1px solid rgba(148, 163, 184, 0.3)';
      ohlcDiv.style.padding = '14px 16px';
      ohlcDiv.style.borderRadius = '12px';
      ohlcDiv.style.fontSize = '12px';
      ohlcDiv.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ohlcDiv.style.zIndex = '10000';
      ohlcDiv.style.pointerEvents = 'none';
      ohlcDiv.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05) inset';
      ohlcDiv.style.minWidth = '160px';
      ohlcDiv.style.display = 'none';
      
      // Ensure the chart container is positioned relative
      if (chartContainerRef.current) {
        chartContainerRef.current.style.position = 'relative';
        chartContainerRef.current.appendChild(ohlcDiv);
      }
      
      return ohlcDiv;
    };

    chart.subscribeCrosshairMove((param) => {
      const ohlc = createOHLCDiv();
      
      if (param.point && param.time) {
        // Try to get data using coordinateToLogical first
        try {
          const logicalIndex = chart.timeScale().coordinateToLogical(param.point.x);
          if (logicalIndex !== null && logicalIndex >= 0 && logicalIndex < marketHoursData.length) {
            const dataPoint = marketHoursData[Math.floor(logicalIndex)];
            
            if (dataPoint && dataPoint.open !== undefined) {
              console.log('Found data point:', dataPoint);
              
              // Format OHLC data
              const o = dataPoint.open?.toFixed(2) || 'N/A';
              const h = dataPoint.high?.toFixed(2) || 'N/A';
              const l = dataPoint.low?.toFixed(2) || 'N/A';
              const c = dataPoint.close?.toFixed(2) || 'N/A';
              
              // Format time
              const date = new Date(param.time * 1000);
              const day = date.getDate().toString().padStart(2, '0');
              const month = date.toLocaleDateString('en-IN', { month: 'short' });
              const hour = date.getHours().toString().padStart(2, '0');
              const minute = date.getMinutes().toString().padStart(2, '0');
              const timeStr = `${day} ${month} ${hour}:${minute}`;
              
              // Determine candle direction
              const isBullish = parseFloat(c) >= parseFloat(o);
              const candleColor = isBullish ? '#22c55e' : '#ef4444';
              
              ohlc.innerHTML = `
                <div style="font-weight: 600; margin-bottom: 10px; color: #f1f5f9; border-bottom: 1px solid rgba(148, 163, 184, 0.2); padding-bottom: 8px; font-size: 13px; letter-spacing: 0.3px;">${timeStr}</div>
                <div style="margin-bottom: 4px; line-height: 1.5;"><span style="color: #94a3b8; width: 22px; display: inline-block; font-weight: 500;">O:</span> <span style="color: #e2e8f0; font-weight: 500;">₹${o}</span></div>
                <div style="margin-bottom: 4px; line-height: 1.5;"><span style="color: #94a3b8; width: 22px; display: inline-block; font-weight: 500;">H:</span> <span style="color: ${isBullish ? '#10b981' : '#e2e8f0'}; font-weight: 500;">₹${h}</span></div>
                <div style="margin-bottom: 4px; line-height: 1.5;"><span style="color: #94a3b8; width: 22px; display: inline-block; font-weight: 500;">L:</span> <span style="color: ${!isBullish ? '#f87171' : '#e2e8f0'}; font-weight: 500;">₹${l}</span></div>
                <div style="line-height: 1.5;"><span style="color: #94a3b8; width: 22px; display: inline-block; font-weight: 500;">C:</span> <span style="color: ${candleColor}; font-weight: 600;">₹${c}</span></div>
              `;
              ohlc.style.display = 'block';
              return;
            }
          }
        } catch (error) {
          console.log('Error getting logical index:', error);
        }
        
        // Fallback: try param.seriesData if available
        if (param.seriesData && param.seriesData.size > 0) {
          const seriesData = param.seriesData.get(series);
          if (seriesData && seriesData.open !== undefined) {
            console.log('Using seriesData fallback:', seriesData);
            
            const o = seriesData.open?.toFixed(2) || 'N/A';
            const h = seriesData.high?.toFixed(2) || 'N/A';
            const l = seriesData.low?.toFixed(2) || 'N/A';
            const c = seriesData.close?.toFixed(2) || 'N/A';
            
            const date = new Date(param.time * 1000);
            const day = date.getDate().toString().padStart(2, '0');
            const month = date.toLocaleDateString('en-IN', { month: 'short' });
            const hour = date.getHours().toString().padStart(2, '0');
            const minute = date.getMinutes().toString().padStart(2, '0');
            const timeStr = `${day} ${month} ${hour}:${minute}`;
            
            const isBullish = parseFloat(c) >= parseFloat(o);
            const candleColor = isBullish ? '#22c55e' : '#ef4444';
            
            ohlc.innerHTML = `
              <div style="font-weight: 600; margin-bottom: 10px; color: #f1f5f9; border-bottom: 1px solid rgba(148, 163, 184, 0.2); padding-bottom: 8px; font-size: 13px; letter-spacing: 0.3px;">${timeStr}</div>
              <div style="margin-bottom: 4px; line-height: 1.5;"><span style="color: #94a3b8; width: 22px; display: inline-block; font-weight: 500;">O:</span> <span style="color: #e2e8f0; font-weight: 500;">₹${o}</span></div>
              <div style="margin-bottom: 4px; line-height: 1.5;"><span style="color: #94a3b8; width: 22px; display: inline-block; font-weight: 500;">H:</span> <span style="color: ${isBullish ? '#10b981' : '#e2e8f0'}; font-weight: 500;">₹${h}</span></div>
              <div style="margin-bottom: 4px; line-height: 1.5;"><span style="color: #94a3b8; width: 22px; display: inline-block; font-weight: 500;">L:</span> <span style="color: ${!isBullish ? '#f87171' : '#e2e8f0'}; font-weight: 500;">₹${l}</span></div>
              <div style="line-height: 1.5;"><span style="color: #94a3b8; width: 22px; display: inline-block; font-weight: 500;">C:</span> <span style="color: ${candleColor}; font-weight: 600;">₹${c}</span></div>
            `;
            ohlc.style.display = 'block';
            return;
          }
        }
      }
      
      // Hide if no valid data found
      ohlc.style.display = 'none';
    });

    // CRITICAL: Force the time scale to be visible and fit content
    setTimeout(() => {
      try {
        chart.timeScale().fitContent();
        chart.timeScale().applyOptions({ visible: true });
      } catch (error) {
        console.error('BaseChart: Error applying time scale options:', error);
      }
    }, 100);

    // Handle resize
    const handleResize = () => {
      if (chart && chartContainerRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: height,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    // Call onChartReady callback if provided
    if (onChartReady) {
      onChartReady(chart, series);
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [data, chartType, height, timeframe, onChartReady]);

  return (
    <div className="w-full" style={{ position: 'relative' }}>
      <div 
        ref={chartContainerRef} 
        className="w-full" 
        style={{ height: `${height}px` }}
      />
    </div>
  );
};

export default BaseChart;
